import doc from './document';
import win from './window';

const availableModes = ['element', 'image', 'background'];
const availableUnits = ['px', 'vw', 'vh', '%'];
const availableAuto = [undefined, 'ttb', 'btt', 'ltr', 'rtl'];
const defaultOptions = {
    mode: availableModes[0],
    unit: availableUnits[0],
    relativeViewport: true,
    x: [0, 0],
    y: [0, 0],
    scale: [1, 1],
    opacity: [1, 1],
    auto: undefined,
    titleOpacity: false,
    titleOpacityDistance: 1000,
    parallaxDelay: 0,
};

let windowHeight = 0;
let elements = [];
let trackLength = 0;
let ticking = false;
let isPaused = false;

export function pauseParallax() {
    isPaused = true;
}

export function resumeParallax() {
    isPaused = false;
    if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
    }
}

function getRelativeScrollPercent(windowScrollTop, elOffsetTop, elHeight, parallaxDelay = 0) {
    const delayOffset = (parallaxDelay / 100) * elHeight;
    const adjustedOffsetTop = elOffsetTop + delayOffset;

    if (windowScrollTop + windowHeight < adjustedOffsetTop) return 0;
    if (elOffsetTop + elHeight < windowScrollTop) return 100;

    if (adjustedOffsetTop < windowHeight) {
        return Math.max(0, (windowScrollTop - delayOffset) / (adjustedOffsetTop + elHeight - delayOffset) * 100);
    } else {
        return Math.max(0, (windowScrollTop - (adjustedOffsetTop - windowHeight)) / (windowHeight + elHeight - delayOffset) * 100);
    }
}

function calculateTitleOpacity(scrollTop, maxDistance) {
    return Math.max(0, 1 - (scrollTop / maxDistance));
}

function parallaxIt(el, elOptions) {
    const scrollTop = win.getScrollTop();
    const elOffsetTop = el.getBoundingClientRect().top + window.pageYOffset;

    let scrolledInPercent = 0;

    if (elOptions.relativeViewport) {
        scrolledInPercent = getRelativeScrollPercent(
            scrollTop,
            elOffsetTop,
            el.offsetHeight,
            elOptions.parallaxDelay
        );
    } else {
        scrolledInPercent = (scrollTop / trackLength * 100);
    }

    if (availableAuto.indexOf(elOptions.auto) !== -1 && elOptions.auto !== undefined) {
        switch (elOptions.auto) {
            case 'ttb': elOptions.y = [0, 100]; elOptions.x = [50, 50]; elOptions.unit = '%'; break;
            case 'btt': elOptions.y = [100, 0]; elOptions.x = [50, 50]; elOptions.unit = '%'; break;
            case 'ltr': elOptions.y = [50, 50]; elOptions.x = [0, 100]; elOptions.unit = '%'; break;
            case 'rtl': elOptions.y = [50, 50]; elOptions.x = [100, 0]; elOptions.unit = '%'; break;
        }
    }

    const x = calcPercentRelatedValueBy(elOptions.x, scrolledInPercent);
    const y = calcPercentRelatedValueBy(elOptions.y, scrolledInPercent);
    const scale = calcPercentRelatedValueBy(elOptions.scale, scrolledInPercent);
    let opacity = calcPercentRelatedValueBy(elOptions.opacity, scrolledInPercent);

    if (elOptions.titleOpacity) {
        opacity = calculateTitleOpacity(scrollTop, elOptions.titleOpacityDistance);
    }

    if (elOptions.mode === 'element') {
        el.style.transform = `translateY(${y}${elOptions.unit}) translateX(${x}${elOptions.unit}) scale(${scale})`;
        el.style.opacity = opacity;
    } else if (elOptions.mode === 'background') {
        el.style.backgroundPosition = `${x}${elOptions.unit} ${y}${elOptions.unit}`;
        el.style.backgroundSize = elOptions.scale[0] !== 1 || elOptions.scale[1] !== 1 ? `${scale * 100}%` : 'cover';
        el.style.opacity = opacity;
    } else if (elOptions.mode === 'image') {
        el.style.objectPosition = `${x}${elOptions.unit} ${y}${elOptions.unit}`;
        el.style.transform = `scale(${scale})`;
        el.style.opacity = opacity;
    }
}

function getElementData(dataset) {
    const options = { ...defaultOptions };

    Object.keys(dataset).forEach(key => {
        if (
            key.startsWith('parallax') ||
            key.startsWith('title') ||
            ['mode', 'unit', 'relativeViewport', 'x', 'y', 'scale', 'opacity', 'auto'].includes(key)
        ) {
            options[key] = dataset[key];
        }
    });

    const optStringsToArray = ['x', 'y', 'scale', 'opacity'];

    Object.keys(options).forEach(key => {
        if (optStringsToArray.includes(key) && typeof options[key] === 'string') {
            options[key] = options[key].split(',').map(x => Number(x.trim()));
        }
        if (key === 'relativeViewport') {
            options[key] = options[key] !== 'false';
        }
        if (key === 'titleOpacity') {
            options[key] = options[key] === 'true' || options[key] === true;
        }
        if (['titleOpacityDistance', 'parallaxDelay'].includes(key)) {
            options[key] = Number(options[key]) || defaultOptions[key];
        }
    });

    if (!availableModes.includes(options.mode)) {
        console.warn(`Parallax: "${options.mode}" is not a valid mode. Using default.`, availableModes);
        options.mode = defaultOptions.mode;
    }
    if (!availableUnits.includes(options.unit)) {
        console.warn(`Parallax: "${options.unit}" is not a valid unit. Using default.`, availableUnits);
        options.unit = defaultOptions.unit;
    }
    if (!availableAuto.includes(options.auto)) {
        console.warn(`Parallax: "${options.auto}" is not a valid auto property. Using default.`, availableAuto);
        options.auto = defaultOptions.auto;
    }
    if (options.parallaxDelay < 0 || options.parallaxDelay > 100) {
        console.warn(`Parallax: parallaxDelay should be between 0-100. Got ${options.parallaxDelay}. Using default.`);
        options.parallaxDelay = defaultOptions.parallaxDelay;
    }

    return options;
}

function update() {
    if (isPaused) return;

    calcTrackLength();
    windowHeight = win.getHeight();

    elements.forEach(element => {
        if (element && element.dataset) {
            parallaxIt(element, getElementData(element.dataset));
        }
    });

    ticking = false;
}

function onScrollHandler() {
    if (isPaused) return;
    if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
    }
}

function calcPercentRelatedValueBy(attribute, scrolledInPercent) {
    if (!Array.isArray(attribute) || attribute.length < 2) {
        console.warn('Parallax: Invalid attribute array', attribute);
        return 0;
    }
    return (scrolledInPercent / 100) * (attribute[1] - attribute[0]) + attribute[0];
}

function calcTrackLength() {
    trackLength = doc.getHeight() - win.getHeight();
}

function fetchDomElements() {
    return Array.from(document.querySelectorAll('.js-parallax'));
}

function initializeParallaxLock() {
    const parallaxImages = document.querySelectorAll('.js-parallax[data-mode="image"]');
    parallaxImages.forEach(img => {
        try {
            const style = window.getComputedStyle(img);
            const objectPosition = style.objectPosition.split(' ');
            if (objectPosition.length >= 2) {
                img.style.setProperty('--parallax-lock-x', objectPosition[0]);
                img.style.setProperty('--parallax-lock-y', objectPosition[1]);
            }
        } catch (error) {
            console.warn('Parallax: Failed to set lock properties for image', img, error);
        }
    });
}

export function destroyParallaxListeners() {
    window.removeEventListener('scroll', onScrollHandler);
    window.removeEventListener('resize', handleResize);
}

function handleResize() {
    calcTrackLength();
    if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
    }
}

export default function init() {
    elements = fetchDomElements();

    if (elements.length === 0) return;

    initializeParallaxLock();
    calcTrackLength();
    update();

    window.addEventListener('scroll', onScrollHandler, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    console.info(`Parallax: Initialized with ${elements.length} elements`);
}
